import { Button, message, Modal, Typography } from "antd";
import { SyncOutlined } from "@ant-design/icons";
import React, { useEffect, useState } from "react";

import { controller as rootController } from "../../../controller";
import {
  PREDETERMINATION_AI_METHODS,
  AI_FETCH_METHODS,
  supportsAny,
  carrierCodeFromPolicy,
} from "../lib/carrierCapabilities";
import SikkaPolicyReviewModal from "../../InsuranceBreakDown/SikkaPolicyReviewModal";
import PolicyCompletionStep, { savePolicyDrafts } from "./shared/PolicyCompletionStep";
import { isPolicyComplete } from "../lib/policyCompleteness";

export default function SelectPolicyItemModal({
  checkPatientInsuranceEligibility,
  setShowPatientInsuranceFormModal,
  setShowSelectPolicyItemModal,
  showSelectPolicyItemModal,
  setSelectedPolicy,
  patientPolicies,
  selectedPolicy,
  setAgentType,
  agentType,
  patientID,
  onPoliciesChanged,
}) {
  const [capabilityMatrix, setCapabilityMatrix] = useState(null);
  const [isFetchingFromSikka, setIsFetchingFromSikka] = useState(false);
  const [sikkaPreview, setSikkaPreview] = useState(null);
  // Second step: the selected policy is missing data the scrape needs. Rather
  // than dispatching and failing at the carrier, collect it here first.
  const [completing, setCompleting] = useState(false);
  const [drafts, setDrafts] = useState({});
  const [savingDrafts, setSavingDrafts] = useState(false);

  // The predetermination + breakdown flows gate rows by carrier capability:
  // each carrier has different supported methods for these operations and we
  // don't want the user to pick a policy whose carrier the AI agent can't
  // service. For every other agent type we short-circuit (load nothing, all
  // rows stay enabled).
  useEffect(() => {
    if (agentType !== "predetermination" && agentType !== "breakdown") return;
    let cancelled = false;
    rootController
      .getCarrierCapabilities()
      .then((matrix) => {
        if (!cancelled) setCapabilityMatrix(matrix);
      })
      .catch((err) => console.error("Failed to load carrier capabilities", err));
    return () => {
      cancelled = true;
    };
  }, [agentType]);

  function isPolicySupported(policy) {
    if (agentType !== "predetermination" && agentType !== "breakdown") return true;
    if (!capabilityMatrix) return true; // Don't blur before we know.
    const carrier = carrierCodeFromPolicy(policy);
    if (agentType === "predetermination") {
      return supportsAny(capabilityMatrix, carrier, "predetermination", PREDETERMINATION_AI_METHODS);
    }
    // agentType === "breakdown"
    return supportsAny(capabilityMatrix, carrier, "breakdown", AI_FETCH_METHODS);
  }

  function dispatchAgent() {
    checkPatientInsuranceEligibility(agentType, selectedPolicy.id);
    setShowSelectPolicyItemModal(false);
  }

  function handleScraperAgentCall() {
    // Gate on the data the scrape actually needs. A missing member ID /
    // contract # / member DOB is what makes the carrier answer "we couldn't
    // find the claimant" minutes later, with nothing to show the user.
    const merged = Object.assign({}, selectedPolicy, drafts[selectedPolicy.id] || {});
    if (!isPolicyComplete(merged)) {
      setCompleting(true);
      return;
    }
    dispatchAgent();
  }

  async function handleCompleteAndFetch() {
    try {
      setSavingDrafts(true);
      const ok = await savePolicyDrafts([selectedPolicy], drafts);
      if (!ok) {
        message.error("Couldn't save the policy details. Please try again.");
        return;
      }
      if (onPoliciesChanged) onPoliciesChanged();
      dispatchAgent();
    } finally {
      setSavingDrafts(false);
    }
  }

  function handleCancelAgent() {
    setShowSelectPolicyItemModal(false);
    setAgentType(undefined);
    setCompleting(false);
    setDrafts({});
  }

  async function handleFetchFromSikka() {
    if (!patientID) {
      message.error("Patient context not available.");
      return;
    }
    try {
      setIsFetchingFromSikka(true);
      const response = await rootController.previewPatientPoliciesFromSikka(patientID);
      if (response.status < 250 && response.json && response.json.preview_token) {
        setSikkaPreview(response.json);
      } else if (response.json && response.json.error_type === "no_insurance_data") {
        message.info("PMS returned no insurance information for this patient.");
      } else {
        const err = (response.json && (response.json.error || response.json.detail)) || "Could not fetch from PMS";
        message.error(err);
      }
    } catch (err) {
      console.error(err);
      message.error("Something went wrong while fetching from PMS.");
    } finally {
      setIsFetchingFromSikka(false);
    }
  }

  function handleSikkaSaved() {
    setSikkaPreview(null);
    if (onPoliciesChanged) onPoliciesChanged();
  }

  const selectionIsSupported = isPolicySupported(selectedPolicy);

  // Measured against the in-progress edits, not the saved policy.
  const completionReady =
    !!selectedPolicy &&
    isPolicyComplete(Object.assign({}, selectedPolicy, drafts[selectedPolicy.id] || {}));

  return (
    <Modal open={showSelectPolicyItemModal} onCancel={handleCancelAgent} footer={null} title="Select Policy Item">
      <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 12 }}>
        <Button
          icon={<SyncOutlined />}
          loading={isFetchingFromSikka}
          onClick={handleFetchFromSikka}
          style={{ background: "#EEEDFA", color: "#6B43B5", borderColor: "#6B43B5" }}
        >
          Fetch from PMS
        </Button>
      </div>
      {completing ? (
        <PolicyCompletionStep
          policies={selectedPolicy ? [selectedPolicy] : []}
          drafts={drafts}
          patientId={patientID}
          onChange={function (policyId, field, value) {
            setDrafts(function (prev) {
              return Object.assign({}, prev, {
                [policyId]: Object.assign({}, prev[policyId], { [field]: value }),
              });
            });
          }}
        />
      ) : patientPolicies.length > 1 ? (
        <div
          style={{ height: 288, maxHeight: 288, display: "flex", flexDirection: "column", gap: 10, overflowY: "auto", padding: 10 }}
          className="custom-scroll"
        >
          {patientPolicies.map((policy) => {
            const supported = isPolicySupported(policy);
            const isSelected = selectedPolicy && policy.id === selectedPolicy.id;
            return (
              <div
                key={policy.id}
                style={{
                  position: "relative",
                  width: "100%",
                  boxShadow: isSelected && supported ? "0 0 10px rgba(107, 67, 181, 0.88)" : "0 0 10px 0 rgba(0, 0, 0, 0.15)",
                  padding: "10px 20px",
                  borderRadius: 5,
                  cursor: supported ? "pointer" : "not-allowed",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  overflow: "hidden",
                }}
                onClick={() => {
                  if (!supported) return;
                  setSelectedPolicy(policy);
                }}
              >
                <div style={{ filter: supported ? "none" : "blur(2px)", opacity: supported ? 1 : 0.6, display: "flex", width: "100%", justifyContent: "space-between" }}>
                  <div>{policy.insurance_name_display}</div>
                  <div>{policy.insurance_type_display}</div>
                  <div>{policy.member_id}</div>
                  <div>{policy.contract_number}</div>
                </div>
                {!supported && (
                  <div
                    style={{
                      position: "absolute",
                      inset: 0,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      background: "rgba(255, 255, 255, 0.55)",
                      color: "#6b43b5",
                      fontWeight: 600,
                      fontSize: 13,
                      letterSpacing: 0.3,
                      pointerEvents: "none",
                    }}
                  >
                    Not supported for this carrier
                  </div>
                )}
              </div>
            );
          })}
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 5 }}>
          <Typography.Title level={5} style={{ marginBottom: 0, color: "#848696" }}>
            You have no policy
          </Typography.Title>
          <Button onClick={() => setShowPatientInsuranceFormModal(true)} type="primary">
            Create Policy
          </Button>
        </div>
      )}

      <div style={{ display: "flex", alignItems: "center", justifyContent: "end", gap: 10, marginTop: 10 }}>
        <Button onClick={() => handleCancelAgent()}>Cancel</Button>

        {completing ? (
          <>
            {patientPolicies.length > 1 ? (
              <Button onClick={() => setCompleting(false)}>Back</Button>
            ) : null}
            <Button
              type="primary"
              loading={savingDrafts}
              disabled={!completionReady}
              onClick={() => handleCompleteAndFetch()}
            >
              Fetch
            </Button>
          </>
        ) : (
          <Button
            type="primary"
            disabled={!(selectedPolicy && selectedPolicy.id) || !selectionIsSupported}
            onClick={() => handleScraperAgentCall()}
          >
            Fetch
          </Button>
        )}
      </div>

      <SikkaPolicyReviewModal
        open={!!sikkaPreview}
        preview={sikkaPreview}
        patientID={patientID}
        onClose={() => setSikkaPreview(null)}
        onSaved={handleSikkaSaved}
      />
    </Modal>
  );
}
